import {
  TTORIA_ASYNC_FEEDBACK_OP_PRIORITY,
  TTORIA_ASYNC_FEEDBACK_TIMING
} from '../../ttoria-async-feedback/src/contract.js';
import { renderDsStack } from '../../../shared-ui/ds/shellColorApi.js';

function immutable(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) immutable(child);
  return Object.freeze(value);
}

function compareActivities(left, right) {
  const priority = Number(TTORIA_ASYNC_FEEDBACK_OP_PRIORITY[left?.opClass] || 0)
    - Number(TTORIA_ASYNC_FEEDBACK_OP_PRIORITY[right?.opClass] || 0);
  if (priority !== 0) return priority;
  return Number(left?.sequence || 0) - Number(right?.sequence || 0);
}

function cleanText(value = '', maxLength = 480) {
  return String(value ?? '')
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/gu, '')
    .replace(/\s+/gu, ' ')
    .trim()
    .slice(0, maxLength);
}

function emptyModel() {
  return immutable({
    visible: false,
    phase: 'idle',
    terminalState: null,
    pendingCount: 0,
    winningActivity: null,
    summary: '',
    detail: '',
    progress: null,
    longRunning: false,
    announcement: '',
    continuousStartedAt: null,
    railShownAt: null
  });
}

export function createTtoriaActivityRailController({
  enabled = false,
  now = () => Date.now(),
  setTimer = (callback, delay) => setTimeout(callback, delay),
  clearTimer = (timerId) => clearTimeout(timerId),
  railDelayMs = TTORIA_ASYNC_FEEDBACK_TIMING.railDelayMs,
  supplementarySuccessMs = TTORIA_ASYNC_FEEDBACK_TIMING.supplementarySuccessMs,
  longRunningMs = TTORIA_ASYNC_FEEDBACK_TIMING.longRunningMs,
  onRender = () => {},
  onAnnouncement = () => {}
} = {}) {
  const snapshots = new Map();
  const detachByKey = new Map();
  const metricsByActivityId = new Map();
  let active = enabled === true;
  let delayTimer = null;
  let longTimer = null;
  let terminalTimer = null;
  let terminalPresentation = null;
  let continuousStartedAt = null;
  let railShownAt = null;
  let railVisible = false;
  let longTimerActivityId = '';
  let previousAnnouncement = '';
  let currentModel = emptyModel();

  function clearPendingTimers() {
    if (delayTimer != null) clearTimer(delayTimer);
    if (longTimer != null) clearTimer(longTimer);
    delayTimer = null;
    longTimer = null;
    longTimerActivityId = '';
  }

  function clearTerminalPresentation() {
    if (terminalTimer != null) clearTimer(terminalTimer);
    terminalTimer = null;
    terminalPresentation = null;
  }

  function activities() {
    return Array.from(snapshots.values())
      .flatMap((snapshot) => Array.isArray(snapshot?.activities) ? snapshot.activities : [])
      .filter((activity) => activity?.state === 'pending' && activity?.silent !== true)
      .sort(compareActivities);
  }

  function emit() {
    const pending = active ? activities() : [];
    const observedAt = Number(now());
    for (const activity of pending) {
      if (!metricsByActivityId.has(activity.id)) {
        metricsByActivityId.set(activity.id, {
          firstFeedbackMs: Math.max(0, observedAt - Number(activity.startedAt || observedAt)),
          railShownAt: null
        });
      }
      if (railVisible && metricsByActivityId.get(activity.id).railShownAt == null) {
        metricsByActivityId.get(activity.id).railShownAt = observedAt;
      }
    }
    const winner = pending.at(-1) || null;
    const winnerLongRunning = Boolean(
      winner
      && observedAt - Number(winner.startedAt || observedAt) >= Math.max(0, Number(longRunningMs) || 0)
    );
    const summary = winner
      ? cleanText(winner.summary, 240)
      : cleanText(terminalPresentation?.summary, 240);
    const baseDetail = winner
      ? cleanText(winner.detail, 480)
      : cleanText(terminalPresentation?.detail, 480);
    const detail = winnerLongRunning
      ? 'Still working. You can continue using available parts of this page.'
      : baseDetail;
    const announcement = winner || terminalPresentation
      ? [summary, detail].filter(Boolean).join('. ')
      : '';
    const phase = winner
      ? 'pending'
      : terminalPresentation?.state === 'failed'
        ? 'failed'
        : terminalPresentation?.state === 'succeeded'
          ? 'succeeded'
          : 'idle';
    currentModel = immutable({
      visible: Boolean(active && ((railVisible && winner) || terminalPresentation)),
      phase,
      terminalState: terminalPresentation?.state || null,
      pendingCount: pending.length,
      winningActivity: winner
        ? { ...winner }
        : terminalPresentation?.activity
          ? { ...terminalPresentation.activity }
          : null,
      summary,
      detail,
      progress: winner?.progress ? { ...winner.progress } : null,
      longRunning: winnerLongRunning,
      announcement,
      continuousStartedAt,
      railShownAt
    });
    onRender(currentModel);
    if (announcement && announcement !== previousAnnouncement) {
      previousAnnouncement = announcement;
      onAnnouncement(announcement);
    } else if (!announcement) {
      previousAnnouncement = '';
    }
  }

  function reconcile() {
    const pending = active ? activities() : [];
    if (pending.length === 0) {
      clearPendingTimers();
      continuousStartedAt = null;
      railShownAt = null;
      railVisible = false;
      emit();
      return;
    }
    if (terminalPresentation) clearTerminalPresentation();
    if (continuousStartedAt == null) {
      continuousStartedAt = Number(now());
      railShownAt = null;
      railVisible = false;
      delayTimer = setTimer(() => {
        delayTimer = null;
        if (!active || activities().length === 0) return;
        railVisible = true;
        railShownAt = Number(now());
        emit();
      }, Math.max(0, Number(railDelayMs) || 0));
    }
    const winner = pending.at(-1) || null;
    if (winner && winner.id !== longTimerActivityId) {
      if (longTimer != null) clearTimer(longTimer);
      longTimer = null;
      longTimerActivityId = winner.id;
      const winnerAgeMs = Math.max(0, Number(now()) - Number(winner.startedAt || Number(now())));
      const remainingMs = Math.max(0, Number(longRunningMs) - winnerAgeMs);
      if (remainingMs > 0) {
        longTimer = setTimer(() => {
          longTimer = null;
          if (!active || activities().length === 0) return;
          emit();
        }, remainingMs);
      }
    }
    emit();
  }

  function attach(scope, key = '') {
    if (!scope || typeof scope.subscribe !== 'function') {
      throw new TypeError('A scoped activity interface is required.');
    }
    const normalizedKey = cleanText(key, 200);
    if (!normalizedKey) throw new TypeError('A stable presenter subscription key is required.');
    detach(normalizedKey);
    const unsubscribe = scope.subscribe({ presenter: 'rail' }, (snapshot) => {
      snapshots.set(normalizedKey, snapshot);
      reconcile();
    });
    detachByKey.set(normalizedKey, unsubscribe);
    return () => detach(normalizedKey);
  }

  function detach(key = '') {
    const normalizedKey = cleanText(key, 200);
    const unsubscribe = detachByKey.get(normalizedKey);
    if (typeof unsubscribe === 'function') unsubscribe();
    detachByKey.delete(normalizedKey);
    snapshots.delete(normalizedKey);
    reconcile();
  }

  function announceTerminal(activity = {}) {
    if (!active || activity?.silent === true) return false;
    const summary = cleanText(activity?.summary, 240);
    const detail = cleanText(activity?.detail, 480);
    const announcement = [summary, detail].filter(Boolean).join('. ');
    if (!announcement) return false;
    previousAnnouncement = announcement;
    onAnnouncement(announcement);
    const state = activity?.state === 'failed'
      ? 'failed'
      : activity?.state === 'succeeded'
        ? 'succeeded'
        : '';
    const metrics = metricsByActivityId.get(cleanText(activity?.id, 200));
    if (!state || metrics?.railShownAt == null || activities().length > 0) return true;
    clearTerminalPresentation();
    terminalPresentation = {
      state,
      activity: { ...activity },
      summary,
      detail: detail || (state === 'succeeded'
        ? 'All systems operational.'
        : 'Please review the message and try again.')
    };
    emit();
    terminalTimer = setTimer(() => {
      terminalTimer = null;
      terminalPresentation = null;
      emit();
    }, Math.max(0, Number(supplementarySuccessMs) || 0));
    return true;
  }

  function setEnabled(nextEnabled) {
    active = nextEnabled === true;
    if (!active) {
      clearPendingTimers();
      clearTerminalPresentation();
      continuousStartedAt = null;
      railShownAt = null;
      railVisible = false;
    }
    reconcile();
  }

  function destroy() {
    clearPendingTimers();
    clearTerminalPresentation();
    for (const unsubscribe of detachByKey.values()) {
      if (typeof unsubscribe === 'function') unsubscribe();
    }
    detachByKey.clear();
    snapshots.clear();
    metricsByActivityId.clear();
    active = false;
    currentModel = emptyModel();
    onRender(currentModel);
  }

  return Object.freeze({
    attach,
    detach,
    announceTerminal,
    setEnabled,
    destroy,
    consumeMetrics(activityId = '', settledAt = Number(now())) {
      const id = cleanText(activityId, 200);
      const metrics = metricsByActivityId.get(id) || { firstFeedbackMs: null, railShownAt: null };
      metricsByActivityId.delete(id);
      const normalizedSettledAt = Number(settledAt);
      return immutable({
        firstFeedbackMs: Number.isFinite(metrics.firstFeedbackMs) ? metrics.firstFeedbackMs : null,
        railShown: metrics.railShownAt != null,
        railDwellMs: metrics.railShownAt == null || !Number.isFinite(normalizedSettledAt)
          ? 0
          : Math.max(0, normalizedSettledAt - metrics.railShownAt)
      });
    },
    snapshot() {
      return currentModel;
    }
  });
}

function escapeHtml(value = '') {
  return String(value ?? '')
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#39;');
}

export function renderTtoriaActivityLaneMarkup(model = {}) {
  const visible = model?.visible === true;
  const phase = ['pending', 'succeeded', 'failed'].includes(model?.phase)
    ? model.phase
    : visible
      ? 'pending'
      : 'idle';
  const pendingVisible = visible && phase === 'pending';
  const pendingCount = Math.max(0, Number(model?.pendingCount || 0));
  const summary = cleanText(model?.summary, 240);
  const detail = cleanText(model?.detail, 480);
  const progress = model?.progress && Number.isFinite(Number(model.progress.value)) && Number.isFinite(Number(model.progress.max))
    ? { value: Number(model.progress.value), max: Number(model.progress.max) }
    : null;
  const progressAttributes = pendingVisible && progress && progress.max > 0 && progress.value >= 0 && progress.value <= progress.max
    ? { 'aria-valuemin': 0, 'aria-valuemax': progress.max, 'aria-valuenow': progress.value }
    : {};
  const progressStyle = progress && progress.max > 0
    ? `--ttoria-async-rail-progress:${Math.max(0, Math.min(100, (progress.value / progress.max) * 100))}%`
    : '';
  const badgeLabel = phase === 'succeeded'
    ? 'DONE'
    : phase === 'failed'
      ? 'FAILED'
      : model?.longRunning === true
        ? 'STILL WORKING'
        : 'WORKING';
  const count = pendingVisible && pendingCount > 1
    ? `<span class="platform-shell-activity-lane__count" aria-label="${pendingCount} activities">${pendingCount}</span>`
    : '';
  return [
    renderDsStack({
      tag: 'div',
      component: 'ds/progress-rail',
      className: 'platform-shell-activity-lane__rail',
      attrs: {
        ...(pendingVisible
          ? { role: 'progressbar', 'aria-label': summary || 'Activity in progress' }
          : { 'aria-hidden': 'true' }),
        ...progressAttributes,
        ...(progressStyle ? { style: progressStyle } : {})
      },
      html: '<span class="platform-shell-activity-lane__rail-value" aria-hidden="true"></span>'
    }),
    renderDsStack({
      tag: 'div',
      component: 'ds/status-row',
      className: 'platform-shell-activity-lane__row',
      html: [
        '<span class="platform-shell-activity-lane__indicator" aria-hidden="true"></span>',
        '<span class="platform-shell-activity-lane__copy">',
        `<span class="platform-shell-activity-lane__summary">${visible ? escapeHtml(summary) : ''}</span>`,
        detail && visible ? `<span class="platform-shell-activity-lane__detail">${escapeHtml(detail)}</span>` : '',
        '</span>',
        visible ? `<span class="platform-shell-activity-lane__badge">${count}${badgeLabel}</span>` : ''
      ].join('')
    })
  ].join('');
}
